import { Dimensions, Groove, GrooveType, MoldType, Profile, RingConfiguration, RingSizeStandard } from '@ringo/types'
import { getStandardSpecificValueFromDiameter } from '../dimensionHelper'
import { checkIfCocAlloy } from '../alloyHelper'
import { AlloyDTO, RingPrice, RingStatus, RingWeight } from '../../generated'
import { getRingDetailsPreviewName, isProfileRing, isSinterring } from '../profileHelper'

type Row = {
    title: string
    value: string | number
}

export type NumberFormatter = (number: number | undefined | null, unit?: 'mm' | '%' | 'g' | 'mm³' | 'kg', fractionDigits?: number) => string

export type TranslationFunc = (
    key:
        | 'processingStatus'
        | 'dimensions.decreaseWall'
        | 'yourBasket.manufacturingCost'
        | 'yourBasket.metalCost'
        | 'detailOverview.unitPrice'
        | 'profile'
        | 'dimensions.width'
        | 'dimensions.wallStrength'
        | 'detailOverview.alloy'
        | 'detailOverview.weight'
        | 'grooves'
        | 'dimensions.ringSize'
        | 'detailOverview.diameter'
        | 'detailOverview.ringBand'
        | 'alloyInformation.coc'
        | 'yes'
        | 'dimensions.molding'
        | 'option.moldType.PLAN'
        | 'option.moldType.BOMBED'
        | 'option.moldType.NONE'
        | 'radius'
        | 'option.edgeType.ROUND'
        | 'option.edgeType.BEVEL'
        | 'dimensions.widthOversize'
        | 'configuration.heightOversize'
        | 'detailOverview.diameterOversize'
        | 'detailOverview.AxialSintering'
        | 'detailOverview.NormalSintering'
        | 'detailOverview.profileType'
        | 'configuration.edgeOuter'
        | 'configuration.edgeInner'
        | 'dimensions.slantAngle'
        | 'type'
        | 'configuration.sinterringType.normal'
        | 'configuration.sinterringType.axial'
        | 'detailOverview.customerProfile'
        | 'to'
) => string

export class RingDetailsBuilder {
    protected rows: Array<Row> = []
    constructor(
        protected ring: RingConfiguration,
        protected visibleFields: Array<keyof Dimensions>,
        protected t: TranslationFunc,
        protected lang: 'en' | 'de',
        protected alloys: AlloyDTO[],
        protected formatCurrency: (number: number) => string,
        protected formatNumber: NumberFormatter
    ) {}

    build() {
        return this.rows
    }

    appendPrice(ringPrice: RingPrice | undefined): RingDetailsBuilder {
        if (!ringPrice) {
            return this
        }

        if (ringPrice.manufacturingCost.totalManufacturingCostWithDiscount) {
            this.rows.push({
                title: this.t('yourBasket.manufacturingCost'),
                value: this.formatCurrency(ringPrice.manufacturingCost.totalManufacturingCostWithDiscount),
            })
        }

        if (ringPrice.metalCost.totalMetalCost) {
            this.rows.push({
                title: this.t('yourBasket.metalCost'),
                value: this.formatCurrency(ringPrice.metalCost.totalMetalCost),
            })
        }

        if (ringPrice.totalRingPrice) {
            this.rows.push({
                title: this.t('detailOverview.unitPrice'),
                value: this.formatCurrency(ringPrice.totalRingPrice),
            })
        }

        return this
    }

    appendFavoriteName(favoriteName: string | undefined): RingDetailsBuilder {
        if (!favoriteName) {
            return this
        }

        this.rows.push({
            title: this.t('detailOverview.customerProfile'),
            value: favoriteName,
        })
        return this
    }

    appendWidth(): RingDetailsBuilder {
        this.rows.push({
            title: this.t('dimensions.width'),
            value: this.formatNumber(this.ring.dimensions.width, 'mm'),
        })
        return this
    }

    appendHeight(withAsterix?: boolean): RingDetailsBuilder {
        if (this.ring.dimensions.heightOversize) {
            this.rows.push({
                title: this.t('dimensions.wallStrength'),
                value: `${this.formatNumber(this.ring.dimensions.height)} (+0 ${this.t('to')} +${this.formatNumber(this.ring.dimensions.heightOversize)}) mm${withAsterix ? '*' : ''}`,
            })
        } else {
            this.rows.push({
                title: this.t('dimensions.wallStrength'),
                value: this.formatNumber(this.ring.dimensions.height, 'mm'),
            })
        }

        return this
    }

    appendRingStatus(ringStatus: RingStatus | undefined) {
        if (!ringStatus) {
            return this
        }

        this.rows.push({
            title: this.t('processingStatus'),
            value: this.lang === 'de' ? ringStatus.Ring_Status_DE! : ringStatus.Ring_Status_EN!,
        })

        return this
    }

    appendWeight(ringWeight: RingWeight | undefined): RingDetailsBuilder {
        if (!ringWeight) {
            return this
        }

        this.rows.push({
            title: this.t('detailOverview.weight'),
            value: ringWeight.weight ? this.formatNumber(ringWeight.weight, 'g') : '',
        })

        return this
    }

    appendGrooves() {
        if (!this.ring.dimensions.grooves?.length) {
            return this
        }

        this.rows.push(
            ...Array.from({ length: this.ring.dimensions.grooves!.length }, (_, index) => ({
                title: index === 0 ? this.t('grooves') : '',
                value: this.formatGrooveName(this.ring.dimensions.grooves[index], this.ring.dimensions.grooveType as GrooveType),
            }))
        )

        return this
    }

    appendAlloy(): RingDetailsBuilder {
        this.rows.push({
            title: this.t('detailOverview.alloy'),
            value: this.ring.dimensions.activeAlloy as string,
        })
        return this
    }

    appendProfileType(): RingDetailsBuilder {
        this.rows.push({
            title: this.t('detailOverview.profileType'),
            value: getRingDetailsPreviewName(this.ring.activeProfile, this.t),
        })

        return this
    }

    appendProfileTypeSpecific(): RingDetailsBuilder {
        if (isProfileRing(this.ring.activeProfile)) {
            this.rows.push({
                title: this.t('profile'),
                value: this.ring.activeProfile,
            })
        } else if (isSinterring(this.ring.activeProfile)) {
            this.rows.push({
                title: this.t('type'),
                value:
                    this.ring.activeProfile === Profile.Sinterring
                        ? this.t('configuration.sinterringType.normal')
                        : this.t('configuration.sinterringType.axial'),
            })
        }

        return this
    }

    appendDiameter(): RingDetailsBuilder {
        let diameter: string | number = this.ring.dimensions.diameter,
            country = '',
            value = '',
            label = ''

        const standardSpecificValue = getStandardSpecificValueFromDiameter(
            this.ring.dimensions.ringSizeStandard,
            this.ring.dimensions.diameter as number,
            this.ring.activeProfile
        )

        switch (this.ring.dimensions.ringSizeStandard) {
            case RingSizeStandard.ENGLAND:
                label = this.t('dimensions.ringSize')
                country = ' (E)'
                diameter = standardSpecificValue.standardValue
                break
            case RingSizeStandard.USA_CANADA:
                label = this.t('dimensions.ringSize')
                country = ' (USA)'
                diameter = standardSpecificValue.standardValue
                break
            case RingSizeStandard.SWITZERLAND:
                label = this.t('dimensions.ringSize')
                country = ' (CH)'
                diameter = standardSpecificValue.standardValue
                break
            case RingSizeStandard.DIAMETER:
                label = this.t('detailOverview.diameter')
                value = 'mm'
                diameter = this.formatNumber(diameter as number)
                break
            case RingSizeStandard.CUSTOM_WIDTH:
                label = this.t('detailOverview.ringBand')
                diameter = standardSpecificValue.standardValue
                break
            default:
                break
        }

        this.rows.push({
            title: label + country,
            value: diameter + ' ' + value,
        })

        return this
    }

    private formatGrooveName(groove: Groove, grooveType: GrooveType): string {
        const name = grooveType === GrooveType.UGroove ? 'U-Form' : 'V-Form'
        const dimensions = this.formatNumber(groove.width) + (grooveType === GrooveType.UGroove ? ' x ' + this.formatNumber(groove.depth!) : '')

        return name + ' ' + '(' + dimensions + ')' + ' Pos. ' + this.formatNumber(groove.position, 'mm')
    }

    appendCoc(): RingDetailsBuilder {
        let hasCoc = false

        if (this.ring.dimensions.slices) {
            const slices = this.ring.dimensions.slices
            hasCoc = slices.some((slice) => checkIfCocAlloy(this.alloys, slice.alloyName!))
        } else if (checkIfCocAlloy(this.alloys, this.ring.dimensions.activeAlloy)) {
            hasCoc = true
        }

        if (hasCoc) {
            this.rows.push({
                title: this.t('alloyInformation.coc'),
                value: this.t('yes'),
            })
        }

        return this
    }

    appendMolding(): RingDetailsBuilder {
        const showMoldOuter = this.visibleFields.includes('moldOuter') && this.ring.dimensions.moldOuter
        const showMoldInner = this.visibleFields.includes('moldInner') && this.ring.dimensions.moldInner

        if (!showMoldInner && !showMoldOuter) {
            return this
        }

        const moldOuterFormatted = showMoldOuter && this.ring.dimensions.moldOuter ? `A: ${this.formatNumber(this.ring.dimensions.moldOuter, '%')}` : ''
        const moldInnerFormatted = showMoldInner && this.ring.dimensions.moldInner ? `I: ${this.formatNumber(this.ring.dimensions.moldInner, '%')}` : ''

        this.rows.push({
            title: `${this.t('dimensions.molding')}${this.visibleFields.includes('moldTypeInner') ? this.t(`option.moldType.${this.ring.dimensions.moldTypeInner!}`) : ''}`,
            value: `${moldOuterFormatted}${showMoldOuter && showMoldInner ? ' ' : ''}${moldInnerFormatted}`,
        })

        return this
    }

    appendSpecialDimensions() {
        if (this.visibleFields.includes('slantAngle') && this.ring.dimensions.slantAngle != null) {
            this.rows.push({
                title: this.t('dimensions.slantAngle'),
                value: this.formatNumber(this.ring.dimensions.slantAngle, '∡' as any),
            })
        }

        if (this.ring.dimensions.moldTypeInner && this.ring.dimensions.moldTypeInner !== MoldType.NONE) {
            this.rows.push({
                title: this.t('dimensions.molding'),
                value: this.t(`option.moldType.${this.ring.dimensions.moldTypeInner}`),
            })
            this.rows.push({
                title: this.t('dimensions.decreaseWall'),
                value: this.formatNumber(this.ring.dimensions.decreaseWall, '%', 0),
            })
        }

        return this
    }

    appendEdges(): RingDetailsBuilder {
        if (this.visibleFields.includes('edgeOuter') && this.ring.dimensions.edgeOuter) {
            this.rows.push({
                title: this.t('configuration.edgeOuter'),
                value: `${this.formatNumber(this.ring.dimensions.edgeOuter)} ${this.t(this.ring.dimensions.edgeTypeOuter as any)}`,
            })
        }

        if (this.visibleFields.includes('edgeInner') && this.ring.dimensions.edgeInner) {
            this.rows.push({
                title: this.t('configuration.edgeInner'),
                value: `${this.formatNumber(this.ring.dimensions.edgeInner)} ${this.t(this.ring.dimensions.edgeTypeInner as any)}`,
            })
        }

        return this
    }

    appendRadius(): RingDetailsBuilder {
        const showRadiusOuter = this.visibleFields.includes('radiusOuter') && this.ring.dimensions.radiusOuter
        const showRadiusInner = this.visibleFields.includes('radiusInner') && this.ring.dimensions.radiusInner

        if (!showRadiusInner && !showRadiusOuter) {
            return this
        }

        const radiusOuterFormatted =
            showRadiusOuter && this.ring.dimensions.radiusOuter ? `A: ${this.formatNumber(this.ring.dimensions.radiusOuter, 'mm')} ` : ''
        const radiusInnerFormatted =
            showRadiusInner && this.ring.dimensions.radiusInner ? `I: ${this.formatNumber(this.ring.dimensions.radiusInner, 'mm')} ` : ''

        this.rows.push({
            title: `${this.t('radius')}`,
            value: `${radiusOuterFormatted}${showRadiusOuter && showRadiusInner ? ' ' : ''}${radiusInnerFormatted}`,
        })

        return this
    }
}
